import React, { useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import Section from '@hero/ui-kit/layout/Section';
import InlineLoader from '../../../../components/InlineLoader';
import QueryInlineError from '../../../../components/QueryInlineError';
import useMarkTemplate from '../../api/useMarkTemplate';
import { MarkPageHeader } from '../../components/MarkLayout';
import TemplateEditorShell from '../../components/BlockEditor/TemplateEditorShell';
import type { MarkTemplateRow } from '../../types';
import * as layout from '../../components/markLayout.module.css';

const NewTemplateDraft: React.FC<{
    duplicate: string | null;
    onSaved: (id: number) => void;
}> = ({ duplicate, onSaved }) => {
    const id = Number(duplicate);
    const validId = duplicate !== null && Number.isSafeInteger(id) && id > 0;
    const [initial, setInitial] = useState<{
        row: MarkTemplateRow | null;
        error: Error | null;
    } | null>(null);
    const query = useMarkTemplate(validId ? id : null, initial === null);
    if (validId && initial === null && !query.isFetching && (query.isSuccess || query.isError))
        setInitial({
            row: query.data?.data ?? null,
            error: query.data ? null : query.error
        });
    if (validId && initial === null) return <InlineLoader />;
    const row = initial?.row;
    const source =
        (row?.format === 'html' || row?.format === 'blocks') &&
        row.channel === 'email' &&
        row.blast_variant_id == null &&
        row.drip_version_id == null
            ? row
            : null;
    const error =
        duplicate === null
            ? null
            : !validId
              ? new Error('Invalid source template ID.')
              : (initial?.error ??
                (!source ? new Error('The source must be an email library template.') : null));
    return (
        <>
            {error && <QueryInlineError error={error} />}
            <TemplateEditorShell
                library
                channel="email"
                templateId={null}
                templateLoadState="missing"
                markTemplate={
                    source
                        ? {
                              format: source.format,
                              blocks: source.blocks,
                              name: Array.from(`Copy of ${source.name ?? ''}`)
                                  .slice(0, 255)
                                  .join(''),
                              html: source.html,
                              subject: source.subject,
                              preheader: source.preheader
                          }
                        : null
                }
                initialDirty={source !== null}
                onSaved={onSaved}
            />
        </>
    );
};

const NewTemplate: React.FC = () => {
    const [params] = useSearchParams();
    const duplicate = params.get('duplicate');
    const location = useLocation();
    const navigate = useNavigate();
    const from = (location.state as { from?: string } | null)?.from ?? '/mark/templates';
    return (
        <Section className={layout.page} subtheme={['regular', 'background']} noDefaultPadding>
            <MarkPageHeader title="Create template">
                <Link to={from}>Back to templates</Link>
            </MarkPageHeader>
            <NewTemplateDraft
                key={duplicate === null ? 'new' : `duplicate-${duplicate}`}
                duplicate={duplicate}
                onSaved={(id) =>
                    navigate(`/mark/templates/${id}`, { replace: true, state: { from } })
                }
            />
        </Section>
    );
};

export default NewTemplate;
